import React, { useContext, useEffect } from "react";
import { HashLink as Link } from "react-router-hash-link";
import { useLocation } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import documentationData from "../../asset/Documenation.json";
import { CanvasContext } from "../../contexts/CanvasContext";
import "./LandingDocumentation.css";
import { FaArrowRight } from "react-icons/fa";
import { RiArrowGoBackFill } from "react-icons/ri";

const Documentation = () => {
  const { isDarkTheme } = useContext(CanvasContext);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const MotionWrapper = ({ children }) => {
    const controls = useAnimation();
    const [ref, inView] = useInView({
      triggerOnce: false,
      threshold: 0.1,
    });

    useEffect(() => {
      if (inView) {
        controls.start("visible");
      } else {
        controls.start("hidden");
      }
    }, [controls, inView]);

    return (
      <motion.div
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={fadeInUpVariants}
      >
        {children}
      </motion.div>
    );
  };

  const renderContent = (content) => {
    if (Array.isArray(content)) {
      return (
        <MotionWrapper>
          <ul>
            {content.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </MotionWrapper>
      );
    }
    return (
      <MotionWrapper>
        <p>{content}</p>
      </MotionWrapper>
    );
  };

  const renderSubsections = (subsections) => {
    return subsections.map((subsection, index) => (
      <MotionWrapper key={index}>
        <div className="subsection">
          <h4>{subsection.title}</h4>
          {renderContent(subsection.content)}
        </div>
      </MotionWrapper>
    ));
  };

  const renderSections = () => {
    return documentationData.sections.map((section, index) => (
      <MotionWrapper key={index}>
        <div id={`${section.id}`} className="section">
          <h3>{section.title}</h3>
          {section.content && renderContent(section.content)}
          {section.subsections && renderSubsections(section.subsections)}
        </div>
      </MotionWrapper>
    ));
  };

  return (
    <section
      id="documentation"
      className={`documentation ${isDarkTheme ? "dark-theme" : ""}`}
    >
      <div className="documentation-container">
        <motion.header
          className="documentation-header"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0, y: -50 },
            visible: { opacity: 1, y: 0, transition: { duration: 1 } },
          }}
        >
          <h2>{documentationData.title}</h2>
        </motion.header>
        <section className="documentation-content">
          <nav className="table-of-contents">
            <h3>Table of Contents</h3>
            {Object.entries(documentationData.TableOfContents).map(
              ([id, content]) => (
                <Link smooth to={`#${id}`} key={id}>
                  {content}
                </Link>
              )
            )}
            <Link to={"/signup"} className="cta-link">
              <p>Get Started</p>
              <i>
                <FaArrowRight />
              </i>
            </Link>
            <Link to={"/"} className="home-link">
              <p>Home</p>
              <i>
                <RiArrowGoBackFill />
              </i>
            </Link>
          </nav>
          <main className="main-content">{renderSections()}</main>
        </section>
      </div>
    </section>
  );
};

export default Documentation;
